import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import * as stylex from "@stylexjs/stylex";
import { accessAPI, queries, errorMessage } from "../lib/api";
import { styles } from "../lib/styles";
import { NavIcon, Drawer, EmptyState, PageHeader } from "../components/Workspace";
import { ServerForm } from "../components/ServerForm";

export const Route = createFileRoute("/servers/$serverId")({ component: ServerDetail });
function ServerDetail() {
  const { serverId } = Route.useParams();
  const state = useQuery(queries.state());
  const [editing, setEditing] = useState(false);
  const cache = useQueryClient();
  const save = useMutation({
    mutationFn: accessAPI.saveServer,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["access"] }),
  });
  const server = state.data?.servers.find((item) => item.id === serverId);
  const deployment = state.data?.deployments.find((item) => item.server_id === serverId);
  if (state.isPending) return <p aria-busy="true">Loading server…</p>;
  if (state.error)
    return (
      <p role="alert" {...stylex.props(styles.error)}>
        {errorMessage(state.error)}
      </p>
    );
  if (!server)
    return (
      <EmptyState
        title="Server not found"
        description="This server may have been removed or is no longer available."
        action={<Link to="/servers">Back to servers</Link>}
      />
    );
  return (
    <>
      <Link to="/servers" className="breadcrumb">
        <NavIcon name="ArrowLeft" /> Servers
      </Link>
      <PageHeader
        title={server.alias}
        description={`${server.login}@${server.address}:${server.port}`}
        action={<Button label="Edit server" variant="primary" onClick={() => setEditing(true)} />}
      />
      <div className="detail-grid">
        <div>
          <section data-workspace-panel {...stylex.props(styles.panel)}>
            <div className="section-heading">
              <h2>Authorization</h2>
              <span
                {...stylex.props(
                  styles.status,
                  server.enabled && deployment?.status === "applied" && styles.applied,
                )}
              >
                {!server.enabled ? "Disabled" : (deployment?.status ?? "Not applied")}
              </span>
            </div>
            {deployment?.error && (
              <p role="alert" {...stylex.props(styles.error)}>
                {deployment.error}
              </p>
            )}
            <dl className="definition-list">
              <dt>Applied identities</dt>
              <dd>
                {deployment?.applied.length
                  ? deployment.applied.map((email) => <p key={email}>{email}</p>)
                  : "No identities applied"}
              </dd>
              <dt>Updated</dt>
              <dd>
                {deployment ? new Date(deployment.updated_at).toLocaleString() : "Not yet deployed"}
              </dd>
            </dl>
            <div className="section-heading">
              <h2>Group rules</h2>
              <Link to="/rules" search={{ view: "groups" }}>
                Manage access
              </Link>
            </div>
            {state.data?.rules
              .filter((rule) => rule.server_id === serverId)
              .map((rule) => (
                <div className="list-row" key={rule.id}>
                  <strong>{rule.group_name}</strong>
                  <span {...stylex.props(styles.status)}>
                    {rule.enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              ))}
          </section>
          <section data-workspace-panel {...stylex.props(styles.panel)}>
            <h2 {...stylex.props(styles.panelHeading)}>Connection</h2>
            <pre {...stylex.props(styles.code)}>ssh {server.alias}</pre>
            <p {...stylex.props(styles.small)}>
              Keep SSBD Access running while you work. Access remains subject to your applied
              grants.
            </p>
          </section>
        </div>
        <section data-workspace-panel {...stylex.props(styles.panel)}>
          <h2 {...stylex.props(styles.panelHeading)}>Configuration</h2>
          <dl className="definition-list">
            <dt>Server ID</dt>
            <dd className="mono">{server.id}</dd>
            <dt>NetBird peer</dt>
            <dd className="mono">{server.peer_id}</dd>
            <dt>Account</dt>
            <dd>{server.login}</dd>
            <dt>Provisioning</dt>
            <dd>{server.provision_user}</dd>
            <dt>Host key</dt>
            <dd className="mono">{server.host_key}</dd>
          </dl>
          <div {...stylex.props(styles.actions)}>
            <Button
              label={server.enabled ? "Disable server" : "Enable server"}
              onClick={() => save.mutate({ ...server, enabled: !server.enabled })}
              isLoading={save.isPending}
            />
          </div>
          {save.error && (
            <p role="alert" {...stylex.props(styles.error)}>
              {errorMessage(save.error)}
            </p>
          )}
          <p {...stylex.props(styles.small)}>
            Changes take effect after successful synchronization. Recovery SSH is preserved.
          </p>
        </section>
      </div>
      {editing && (
        <Drawer title="Edit server" onClose={() => setEditing(false)}>
          <ServerForm server={server} onSaved={() => setEditing(false)} />
        </Drawer>
      )}
    </>
  );
}
